import { forwardRef } from "react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { X } from "lucide-react";
import { cn } from "@ds/lib/utils";

/**
 * Sheet — drawer. Komposisi adaptif: keranjang = Sheet bawah di mobile
 * (zona jempol), panel/ Sheet kanan di desktop. Komponen sama, side berbeda.
 */
export const Sheet = DialogPrimitive.Root;
export const SheetTrigger = DialogPrimitive.Trigger;
export const SheetClose = DialogPrimitive.Close;

type Side = "right" | "bottom";

const sideClasses: Record<Side, string> = {
  right:
    "inset-y-0 right-0 h-full w-[min(26rem,calc(100vw-3rem))] border-l rounded-l-lg sheet-right-in",
  bottom:
    "inset-x-0 bottom-0 max-h-[88vh] w-full border-t rounded-t-xl sheet-bottom-in",
};

export const SheetContent = forwardRef<
  React.ElementRef<typeof DialogPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content> & {
    side?: Side;
    hideClose?: boolean;
  }
>(({ className, children, side = "right", hideClose, ...props }, ref) => (
  <DialogPrimitive.Portal>
    <DialogPrimitive.Overlay className="z-backdrop fixed inset-0 bg-black/45 backdrop-blur-[2px] overlay-in" />
    <DialogPrimitive.Content
      ref={ref}
      className={cn(
        "z-modal fixed flex flex-col border-border bg-surface shadow-[var(--shadow-pop)] focus:outline-none",
        sideClasses[side],
        className,
      )}
      {...props}
    >
      {side === "bottom" && (
        <div
          aria-hidden
          className="mx-auto mt-2 h-1 w-10 shrink-0 rounded-full bg-border-strong"
        />
      )}
      {children}
      {!hideClose && (
        <DialogPrimitive.Close
          className="absolute right-3 top-3 flex size-8 items-center justify-center rounded-md text-muted transition-colors hover:bg-faint hover:text-ink focus-visible:outline-none"
          aria-label="Tutup"
        >
          <X className="size-4" aria-hidden />
        </DialogPrimitive.Close>
      )}
    </DialogPrimitive.Content>
  </DialogPrimitive.Portal>
));
SheetContent.displayName = "SheetContent";

export function SheetHeader({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={cn("flex flex-col gap-1 border-b border-border p-4 pr-10", className)}
      {...props}
    />
  );
}

export function SheetTitle({
  className,
  ...props
}: React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title>) {
  return (
    <DialogPrimitive.Title
      className={cn("text-base font-semibold text-ink", className)}
      {...props}
    />
  );
}

export function SheetBody({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) {
  return <div className={cn("flex-1 overflow-y-auto p-4", className)} {...props} />;
}

export function SheetFooter({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={cn(
        "flex gap-2 border-t border-border p-4 [&>*]:flex-1",
        className,
      )}
      {...props}
    />
  );
}
